import { Flex, Text, Input, Button } from '@chakra-ui/react';
import { useField } from 'formik';
import { useSelector } from 'react-redux';
import { selectUser } from '@/store/bootstrap/selectors';
import { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { DayPicker, DateRange } from 'react-day-picker';
import PopoverWrapper from '@/ui/popover/popoverwrapper';

export const FilterDateRangePicker = ({ item, field }) => {
  const { name, isCustomize, containerRef, showAllDates = false } = item;
  const [, meta, helpers] = useField(name);
  const { setValue, setTouched, setError } = helpers;
  const { value } = field || {};
  const { error, touched } = meta;
  const currUser = useSelector(selectUser);
  const { date_fmt } = currUser || {};
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [tempDateRange, setTempDateRange] = useState<DateRange | undefined>();

  useEffect(() => {
    if (!tempDateRange && meta?.value) {
      setTempDateRange({
        from: new Date(meta?.value?.from),
        to: new Date(meta?.value?.to),
      });
    }
  }, [meta?.value, tempDateRange]);

  const [displayValue, setDisplayValue] = useState('');
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const dateFormats = [
    { label: 'MM/DD/YYYY', value: 'MM-DD-YYYY' },
    { label: 'DD/MM/YYYY', value: 'DD-MM-YYYY' },
    { label: 'YYYY/MM/DD', value: 'YYYY-MM-DD' },
    { label: 'DD MMMM YYYY', value: 'DD MMMM YYYY' },
    { label: 'MMMM DD YYYY', value: 'MMMM DD YYYY' },
    { label: 'YYYY MMMM DD', value: 'YYYY MMMM DD' },
  ];

  const formatDate = (date, date_fmt) => {
    if (!date) return '';
    const formatObj = dateFormats.find((format) => format.value === date_fmt);
    const format = formatObj ? formatObj.label : 'MM/DD/YYYY';
    return dayjs(date).isValid() ? dayjs(date).format(format) : '';
  };

  const formatDateRange = (range) => {
    if (!range?.from || !range?.to) return '';
    const fromDate = formatDate(range.from, date_fmt);
    const toDate = formatDate(range.to, date_fmt);
    return `${fromDate} - ${toDate}`;
  };

  const setFieldProps = (range) => {
    if (!range) return;

    const newRange = {
      from: range.from ? dayjs(range.from).format('YYYY-MM-DD') : null,
      to: range.to ? dayjs(range.to).format('YYYY-MM-DD') : null,
    };

    setDateRange(range);
    const formattedRange = formatDateRange(range);
    setDisplayValue(formattedRange);
    setValue(newRange);
    setTouched(true);
    setError(null);
  };

  const handleApply = () => {
    if (tempDateRange) {
      setFieldProps(tempDateRange);
    }
    setIsDatePickerOpen(false);
  };

  const handleCancel = () => {
    setTempDateRange(dateRange);
    setIsDatePickerOpen(false);
  };

  const isError = touched && error && (!value?.from || !value?.to);

  const getPlaceholder = (date_fmt) => {
    const formatObj = dateFormats.find((format) => format.value === date_fmt);
    const format = formatObj ? formatObj.label : 'MM/DD/YYYY';
    return `${format} - ${format}`;
  };

  const triggerElement = (
    <Input
      fontSize="14px"
      placeholder={getPlaceholder(date_fmt)}
      value={displayValue || formatDateRange(meta?.value)}
      id={name}
      isInvalid={isError}
      borderRadius="md"
      _hover={{
        border: 'transparent',
      }}
      _focus={{
        outline: 'none',
      }}
      onClick={() => setIsDatePickerOpen(true)}
      onFocus={(e) => e.preventDefault()}
      readOnly
    />
  );

  const popoverContent = (
    <Flex direction="column">
      <DayPicker
        mode="range"
        className="schedule-date-picker filter-datepicker"
        numberOfMonths={2}
        selected={tempDateRange}
        onSelect={(range: DateRange | undefined) => {
          if (!range) return;
          setTempDateRange(range);
        }}
        disabled={isCustomize || showAllDates ? [] : [{ after: new Date() }]}
        toMonth={showAllDates ? undefined : new Date()}
        fromYear={2010} // Set the start year for the dropdown
        toYear={
          showAllDates
            ? new Date().getFullYear() + 10
            : new Date().getFullYear()
        } // Allow future years when showAllDates is true
        captionLayout="dropdown"
      />
      <Flex
        justify="flex-end"
        gap="2"
        mt="3"
        borderTop="1px solid"
        borderColor="gray.200"
        pt="3"
      >
        <Button variant="ats-secondary" onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          variant="ats-primary"
          onClick={handleApply}
          isDisabled={!tempDateRange?.from || !tempDateRange?.to}
        >
          Apply
        </Button>
      </Flex>
    </Flex>
  );

  return (
    <Flex flexDir="column" w="100%">
      <PopoverWrapper
        containerRef={containerRef}
        trigger={triggerElement}
        content={popoverContent}
        isOpen={isDatePickerOpen}
        onClose={handleCancel}
      />
      {isError && (
        <Text color="ats-negative" fontSize="sm" mt="8px">
          {error}
        </Text>
      )}
    </Flex>
  );
};
